import { useEffect, useRef, useState } from 'react'
import { Button } from './Button'
import type { ButtonProps } from './Button'

export interface ConfirmButtonProps extends Omit<ButtonProps, 'onClick'> {
  onConfirm: () => void
  confirmLabel?: string
}

/**
 * Two-step destructive action: one click arms it, a second click within four
 * seconds commits. Cheaper than a modal for clear-history-scale actions, and
 * it cannot be triggered by a single stray click.
 */
export function ConfirmButton({
  onConfirm,
  children,
  confirmLabel = 'Click again to confirm',
  variant = 'ghost',
  size = 'sm',
  ...props
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const handleClick = () => {
    if (armed) {
      if (timerRef.current) clearTimeout(timerRef.current)
      setArmed(false)
      onConfirm()
      return
    }

    setArmed(true)
    timerRef.current = setTimeout(() => setArmed(false), 4000)
  }

  return (
    <Button
      variant={armed ? 'danger' : variant}
      size={size}
      onClick={handleClick}
      onBlur={() => setArmed(false)}
      {...props}
    >
      {armed ? confirmLabel : children}
    </Button>
  )
}
